import { useEffect, useRef, useState } from "react";
import { Send, MessagesSquare } from "lucide-react";
import { API, addrList, hoursList, waList } from "@/lib/api";
import WhatsAppPicker from "@/components/WhatsAppPicker";
import { usePageMeta } from "@/lib/seo";

const SUGGESTIONS = [
  "Apa saja produk yang dijual NAWADAH by NDH Official?",
  "Bagaimana cara memesan?",
  "Berapa harga tali PP Lumba Lumba?",
  "Lokasi tokonya di mana saja?",
  "Jam operasionalnya kapan?",
  "Bisa kirim ke luar kota?",
];

const faqAnswer = (question, site) => {
  const q = question.toLowerCase();
  const addresses = addrList(site);
  if (/produk|jual|tersedia|katalog/.test(q)) {
    return "NAWADAH by NDH Official menyediakan Tali PP merek LUMBA LUMBA dan KUDA EMAS, Katrol LUMBA LUMBA, serta timbangan manual dan digital. Detail varian dan stok terbaru bisa dilihat di menu Produk atau Toko.";
  }
  if (/cara.*(pesan|beli)|pemesanan|order|checkout/.test(q)) {
    return "Untuk memesan, buka menu Toko, pilih produk dan jumlah yang dibutuhkan, masukkan ke keranjang, lalu lanjutkan checkout. Jika ingin dibantu memilih ukuran atau produk, lanjutkan chat dengan salah satu admin WhatsApp di bawah.";
  }
  if (/harga|berapa|biaya/.test(q)) {
    return "Harga mengikuti jenis, ukuran, jumlah, dan stok terbaru. Silakan cek varian di menu Toko. Untuk penawaran jumlah banyak atau memastikan harga hari ini, pilih admin WhatsApp di bawah ya, Kak.";
  }
  if (/lokasi|alamat|toko.*mana|dimana|di mana/.test(q)) {
    return addresses.length
      ? `Lokasi NAWADAH: ${addresses.map((item) => `${item.label ? `${item.label}: ` : ""}${item.address}`).join("; ")}. Sebelum datang, sebaiknya konfirmasi lebih dulu kepada admin WhatsApp.`
      : "NAWADAH melayani pelanggan dari Indonesia. Untuk alamat kunjungan atau titik pengambilan terbaru, silakan konfirmasi langsung kepada admin WhatsApp.";
  }
  if (/jam|buka|operasional|tutup/.test(q)) {
    return `Jam operasional kami: ${hoursList(site).map((item) => item.label ? `${item.label}: ${item.hours}` : item.hours).join("; ")}. Di luar jam tersebut pesan tetap bisa dikirim dan akan dibalas saat admin kembali aktif.`;
  }
  if (/kirim|pengiriman|luar kota|ongkir|ekspedisi/.test(q)) {
    return "Bisa, kami melayani pengiriman ke berbagai wilayah Indonesia. Ongkir dan pilihan ekspedisi menyesuaikan alamat, berat, dan ukuran pesanan. Admin akan membantu mengecek pilihan pengiriman yang paling sesuai.";
  }
  if (/bayar|pembayaran|transfer|qris|rekening/.test(q)) {
    return "Metode pembayaran yang tersedia dapat mencakup transfer bank, QRIS, atau tunai sesuai pesanan. Detail rekening dan total resmi diberikan pada proses checkout atau oleh admin NAWADAH.";
  }
  if (/tali|lumba|kuda|katrol|timbangan/.test(q)) {
    return "Kami siap membantu memilih produk sesuai kebutuhan, termasuk jenis tali, ukuran, katrol, dan timbangan. Sebutkan penggunaan, ukuran, serta jumlah yang dicari agar admin bisa memberi rekomendasi yang tepat.";
  }
  return "Terima kasih, Kak. Untuk informasi produk, harga, stok, pemesanan, lokasi, atau pengiriman, silakan tulis pertanyaannya lebih spesifik. Kakak juga bisa langsung lanjut chat dengan salah satu admin WhatsApp di bawah.";
};

export default function Faq() {
  usePageMeta("FAQ Produk & Pemesanan", "Tanya jawab seputar bisnis & produk NAWADAH by NDH Official: tali PP, katrol, timbangan, pemesanan, dan pengiriman.");
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Halo Kak! Selamat datang di NAWADAH by NDH Official. Ada yang bisa kami bantu seputar produk tali tambang, katrol, atau timbangan? Silakan pilih pertanyaan di bawah atau ketik langsung ya." },
  ]);
  const [input, setInput] = useState("");
  const [site, setSite] = useState({});
  const [waQuestion, setWaQuestion] = useState("");
  const bottomRef = useRef(null);

  useEffect(() => {
    API.get("/settings").then((response) => setSite(response.data || {})).catch(() => {});
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const ask = (question) => {
    const q = (question || input).trim();
    if (!q) return;
    setInput("");
    setMessages((current) => [
      ...current,
      { role: "user", text: q },
      { role: "assistant", text: faqAnswer(q, site), question: q, canContinue: true },
    ]);
  };

  return (
    <div className="relative min-h-screen bg-navy-dark bg-maritime-grid noise-overlay text-white py-14" data-testid="faq-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-gold mb-3">Tanya Jawab</p>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight">FAQ NAWADAH by NDH Official</h1>
          <p className="text-slate-400 text-sm mt-3">Tanya apa saja seputar produk &amp; layanan kami — dijawab langsung seperti chat dengan admin.</p>
        </div>

        <div className="card-maritime-surface rounded-md overflow-hidden">
          <div className="h-[420px] overflow-y-auto p-5 space-y-4" data-testid="faq-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`faq-msg-${i}`}>
                <div
                  className={`max-w-[85%] px-4 py-3 rounded-md text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user" ? "bg-gold text-[#0B1F3A] font-medium" : "bg-[#040F1D] border border-white/10 text-slate-200"
                  }`}
                >
                  {m.text}
                  {m.role === "assistant" && m.canContinue && (
                    <button type="button" onClick={() => setWaQuestion(m.question || "")} className="mt-3 flex w-full items-center justify-center gap-2 rounded-md border border-[#25D366]/50 bg-[#25D366]/10 px-3 py-2 text-xs font-bold text-[#65E591] transition-colors hover:bg-[#25D366]/20" data-testid={`faq-continue-wa-${i}`}>
                      Lanjut chat di WhatsApp
                    </button>
                  )}
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          <div className="border-t border-white/10 p-4">
            <div className="flex flex-wrap gap-2 mb-4">
              {SUGGESTIONS.map((s, i) => (
                <button
                  key={s}
                  onClick={() => ask(s)}
                  data-testid={`faq-suggestion-${i}`}
                  className="px-3.5 py-2 text-xs font-semibold border border-white/15 rounded-full text-slate-300 hover:border-gold hover:text-gold transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
            <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="flex gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                data-testid="faq-input"
                placeholder="Ketik pertanyaan Anda..."
                className="flex-1 bg-[#040F1D] border border-white/15 rounded-md px-4 py-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
              />
              <button
                type="submit"
                disabled={!input.trim()}
                data-testid="faq-send"
                className="px-5 py-3 bg-gold bg-gold-hover text-[#0B1F3A] font-bold text-sm rounded-md transition-colors disabled:opacity-50"
              >
                <Send className="h-4 w-4" />
              </button>
            </form>
            <p className="text-[11px] text-slate-500 mt-3 flex items-center gap-1.5">
              <MessagesSquare className="h-3.5 w-3.5" />
              Butuh jawaban lebih detail?{" "}
              <button onClick={() => setWaQuestion("Saya ingin bertanya lebih lanjut mengenai produk dan layanan NAWADAH.")} data-testid="faq-wa-link" className="text-gold hover:underline">
                Chat admin via WhatsApp
              </button>
            </p>
          </div>
        </div>
      </div>

      <WhatsAppPicker
        open={Boolean(waQuestion)}
        onClose={() => setWaQuestion("")}
        contacts={waList(site)}
        message={`Halo Admin NAWADAH, saya ingin lanjut bertanya: ${waQuestion}`}
      />
    </div>
  );
}
